// Day-level actions on the program Draft: duplicate, copy onto, move, import
// a saved workout or another program's day into a day, and the phone
// editor's selection (#336): copy exercises into other days, remove several.
// Split from programEditActions.ts (389 lines, cap 350) and re-exported by
// the barrel like the Group / Note siblings. Pure `(draft, …) => Draft`.
//
// ⛔ The Draft holds no per-day index anywhere else: weeks live INSIDE each
// slot (cells, inactiveWeeks), groups are keyed by id within one session, and
// `sessionsPerWeek` is a count. So moving or copying a day needs no
// re-pointing; the only thing to keep coherent is the count (see duplicate).
import type { Draft, SessionDraft, SlotDraft } from "./programEdit.js";
import { cloneSlotDraft, hasActiveWeek } from "./programBlocks.js";
import {
  appendSlots,
  MAX_SESSIONS,
  resizeSlotToWeeks,
  sessionsPerWeekAfterAdd,
} from "./programEditActions.js";
import { programToDraft } from "./programEditRead.js";
import { changeEditLocale } from "./programEditLocale.js";
import { MAX_DAY_EXERCISES } from "./programEditProblems.js";
import { groupRunAt } from "./programEditActionsGroup.js";
import { planToSlotDrafts } from "./planEdit.js";
import type { Plan as PlanT } from "./schemas/plan.js";
import type { Program as ProgramT } from "./schemas/program.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { uuidv4 } from "./uuid.js";

/** Independent copy of a slot list. Every slot gets a fresh id (brief §8.4: no
 *  link to the origin). A group id is shared by its members, so it is
 *  re-minted ONCE per old id and the copy stays a superset of itself. The deep
 *  copy is `cloneSlotDraft`, the one slot copier. */
const copySlots = (slots: SlotDraft[]): SlotDraft[] => {
  const groupIds = new Map<string, string>();
  return slots.map((s) => {
    let group = s.group;
    if (group) {
      let id = groupIds.get(group.id);
      if (!id) {
        id = uuidv4();
        groupIds.set(group.id, id);
      }
      group = { ...group, id };
    }
    return { ...cloneSlotDraft(s), id: uuidv4(), group };
  });
};

/** How many chosen exercises a "copy onto" would erase from day `sIdx`. A
 *  slot with no exercise picked is an empty placeholder, not a loss. 0 means
 *  the UI can skip the confirm. Out of range reads as 0. */
export const sessionExerciseCount = (draft: Draft, sIdx: number): number =>
  (draft.sessions[sIdx]?.slots ?? []).filter((s) => s.exerciseId !== "").length;

/** A new day right after `sIdx`, a full independent copy. The NAME is the
 *  original's, unchanged: the logic holds no user-facing text, so the UI
 *  suffixes it (via `updateSession`) in the athlete's language. The new day is
 *  at `sIdx + 1`. At MAX_SESSIONS it returns the draft unchanged. */
export const duplicateSession = (draft: Draft, sIdx: number): Draft => {
  const src = draft.sessions[sIdx];
  if (!src || draft.sessions.length >= MAX_SESSIONS) return draft;
  const copy: SessionDraft = { ...src, id: uuidv4(), slots: copySlots(src.slots) };
  return {
    ...draft,
    sessions: [
      ...draft.sessions.slice(0, sIdx + 1),
      copy,
      ...draft.sessions.slice(sIdx + 1),
    ],
    // Same rule as addSession: more days implies at least that many per week.
    sessionsPerWeek: sessionsPerWeekAfterAdd(draft),
  };
};

/** Replace the content (slots, notes) of day `toIdx` with a copy of day
 *  `fromIdx`. `toIdx` keeps its id and name. Ask `sessionExerciseCount(draft,
 *  toIdx)` first for the confirm. Same index or out of range: unchanged. */
export const copySessionOnto = (
  draft: Draft,
  fromIdx: number,
  toIdx: number,
): Draft => {
  const src = draft.sessions[fromIdx];
  if (!src || !draft.sessions[toIdx] || fromIdx === toIdx) return draft;
  return {
    ...draft,
    sessions: draft.sessions.map((s, i) => {
      if (i !== toIdx) return s;
      const { notesRaw: _drop, ...rest } = s;
      return {
        ...rest,
        notes: src.notes,
        ...(src.notesRaw ? { notesRaw: src.notesRaw } : {}),
        slots: copySlots(src.slots),
      };
    }),
  };
};

/** Swap day `sIdx` with its neighbour; no-op at the edges (like moveSlot). */
export const moveSession = (draft: Draft, sIdx: number, delta: -1 | 1): Draft => {
  const target = sIdx + delta;
  if (!draft.sessions[sIdx] || target < 0 || target >= draft.sessions.length) {
    return draft;
  }
  const next = [...draft.sessions];
  next[sIdx] = draft.sessions[target]!;
  next[target] = draft.sessions[sIdx]!;
  return { ...draft, sessions: next };
};

/** A saved workout imported at the end of day `sIdx` (PE-40, and the phone
 *  editor's Picker since #336): its exercises, the same work in every round.
 *  ⛔ The cycle length is read off the draft handed in, so inside a setDraft
 *  updater it is the live one: a plan imported right after a round was added
 *  fans out over the new length, not over a length captured earlier. */
export const appendPlanToDay = (
  draft: Draft,
  sIdx: number,
  plan: PlanT,
  cellUnit: UnitsPreference,
): Draft => appendSlots(draft, sIdx, planToSlotDrafts(plan, cellUnit, draft.cycleWeeks));

/** The ids of `slotIds` grown to whole supersets: a member stands for its
 *  group (the adjacent run, as groupRunAt reads it). In day order. */
const withGroups = (slots: readonly SlotDraft[], slotIds: readonly string[]): Set<string> => {
  const wanted = new Set(slotIds);
  const out = new Set<string>();
  slots.forEach((s, i) => {
    if (!wanted.has(s.id)) return;
    const [a, b] = groupRunAt(slots, i);
    for (let j = a; j <= b; j++) out.add(slots[j]!.id);
  });
  return out;
};

/** A superset left with one member in a list about to land on a day (a
 *  member filtered out, or a group split in the data) stops being one: its
 *  `group` is cleared, as the save would do. Run on the copy, never on the
 *  day it joins: copySlots mints the group ids again, so the copy cannot
 *  join a group already there. */
const dropLoneGroups = (slots: SlotDraft[]): SlotDraft[] => {
  const count = new Map<string, number>();
  for (const s of slots) if (s.group) count.set(s.group.id, (count.get(s.group.id) ?? 0) + 1);
  return slots.map((s) => (s.group && (count.get(s.group.id) ?? 0) < 2 ? { ...s, group: null } : s));
};

/** #336: the days of `days` that cannot take `adding` more exercises without
 *  passing MAX_DAY_EXERCISES (the schema's cap). The writes below skip them;
 *  the editor names them. */
export const daysOverCap = (draft: Draft, days: readonly number[], adding: number): number[] =>
  days.filter((d) => (draft.sessions[d]?.slots.length ?? 0) + adding > MAX_DAY_EXERCISES);

/** How many exercises copySlotsToDays brings into each day: the selection
 *  with its supersets made whole. */
export const copySlotCount = (draft: Draft, fromS: number, slotIds: readonly string[]): number =>
  withGroups(draft.sessions[fromS]?.slots ?? [], slotIds).size;

/** #336: the exercises `slotIds` of day `fromS`, copied to the END of every
 *  day in `toS`, in their order. A superset member brings its whole group.
 *  Each target gets its own independent copy (copySlots: fresh ids, group ids
 *  minted again per day; the stored per-language texts travel with it). A
 *  target that is the source, out of range, or that the copy would take past
 *  MAX_DAY_EXERCISES is skipped; nothing to copy: the draft, unchanged. */
export const copySlotsToDays = (
  draft: Draft,
  fromS: number,
  slotIds: readonly string[],
  toS: readonly number[],
): Draft => {
  const slots = draft.sessions[fromS]?.slots ?? [];
  const ids = withGroups(slots, slotIds);
  const picked = slots.filter((s) => ids.has(s.id));
  if (picked.length === 0) return draft;
  const full = new Set(daysOverCap(draft, toS, picked.length));
  return toS.reduce(
    (d, t) =>
      t === fromS || full.has(t) ? d : appendSlots(d, t, dropLoneGroups(copySlots(picked))),
    draft,
  );
};

/** #336: remove the exercises `slotIds` from day `sIdx`, each with the rest
 *  of its superset (the selection takes groups whole, and so does this).
 *  Unknown ids are ignored. A round the day is left empty in is the
 *  warnings panel's «emptyCell», as for any other removal. */
export const removeSlots = (draft: Draft, sIdx: number, slotIds: readonly string[]): Draft => {
  const session = draft.sessions[sIdx];
  if (!session) return draft;
  const ids = withGroups(session.slots, slotIds);
  if (ids.size === 0) return draft;
  return {
    ...draft,
    sessions: draft.sessions.map((s, i) =>
      i === sIdx ? { ...s, slots: s.slots.filter((sl) => !ids.has(sl.id)) } : s,
    ),
  };
};

/** Which rounds each exercise of day `srcIdx` runs in, read straight off the
 *  stored program or the draft. */
const activeRoundsOf = (src: ProgramT | Draft, srcIdx: number): boolean[][] =>
  "createdAt" in src
    ? (src.sessions[srcIdx]?.slots ?? []).map((sl) =>
        Array.from({ length: src.cycleWeeks }, (_, w) => (sl.weeks[w]?.sets.length ?? 0) > 0),
      )
    : (src.sessions[srcIdx]?.slots ?? []).map((sl) =>
        Array.from({ length: src.cycleWeeks }, (_, w) => !sl.inactiveWeeks[w]),
      );

/** #85: the rounds (0-based) of day `srcIdx` that had work and fall beyond a
 *  cycle of `cycleWeeks`: what an import leaves out, for the toast. */
export const droppedRoundsOf = (
  src: ProgramT | Draft,
  srcIdx: number,
  cycleWeeks: number,
): number[] => {
  const rounds = activeRoundsOf(src, srcIdx);
  const out: number[] = [];
  for (let w = cycleWeeks; w < src.cycleWeeks; w++) {
    if (rounds.some((r) => r[w])) out.push(w);
  }
  return out;
};

/** #85: how many exercises an import of day `srcIdx` brings into a cycle of
 *  `cycleWeeks`: those that run in at least one round of it. 0: nothing. */
export const importedSlotCount = (
  src: ProgramT | Draft,
  srcIdx: number,
  cycleWeeks: number,
): number => activeRoundsOf(src, srcIdx).filter((r) => r.slice(0, cycleWeeks).some(Boolean)).length;

/** #85 (#336, 2B): day `srcIdx` of another program, appended at the end of
 *  day `toIdx`. A stored Program is read through programToDraft, then (like a
 *  Draft) moved to THIS draft's writing language (changeEditLocale): every
 *  stored language travels as it is, the text on screen is this language's,
 *  so the save cannot file one language's words under another. Then an
 *  independent copy (copySlots: fresh ids, groups minted again) fitted to
 *  this cycle: rounds it lacks are EMPTY (resizeSlotToWeeks "blank", the
 *  exercise does not run there), rounds beyond it are dropped, and so is an
 *  exercise that ran only there (a superset it leaves alone is undone).
 *  Nothing left, or a day it would take past MAX_DAY_EXERCISES: the draft,
 *  unchanged. droppedRoundsOf and importedSlotCount answer the toast. */
export const importSessionSlots = (
  draft: Draft,
  toIdx: number,
  src: ProgramT | Draft,
  srcIdx: number,
  cellUnit: UnitsPreference,
): Draft => {
  const target = draft.editLocale ?? undefined;
  const read = "createdAt" in src ? programToDraft(src, cellUnit, target) : src;
  const from = target ? changeEditLocale(read, target) : read;
  const fitted = dropLoneGroups(
    copySlots(from.sessions[srcIdx]?.slots ?? [])
      .map((s) => resizeSlotToWeeks(s, draft.cycleWeeks, "blank"))
      .filter(hasActiveWeek),
  );
  if (fitted.length === 0 || daysOverCap(draft, [toIdx], fitted.length).length > 0) return draft;
  return appendSlots(draft, toIdx, fitted);
};
